import graylogService from 'services/GraylogService';

class GlobalErrorHandler {
    constructor() {
        this.isInitialized = false;
        this.originalConsoleError = console.error;
        this.originalConsoleWarn = console.warn;
    }

    /**
     * Initialize global error handlers
     */
    initialize() {
        if (this.isInitialized) {
            return;
        }

        // Handle unhandled JavaScript errors
        window.addEventListener('error', this.handleGlobalError);

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

        // Override console methods to capture errors and warnings
        this.overrideConsoleMethods();

        // Removed page visibility and unload logging - keeping only essential error logging

        this.isInitialized = true;
        console.log('Global error handlers initialized');
    }

    /**
     * Handle global JavaScript errors
     */
    handleGlobalError = (event) => {
        const error = event.error || new Error(event.message || 'Unknown error');
        const errorInfo = {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            type: 'global_error'
        };

        graylogService.logError(error, errorInfo);

        // Call original console.error for development
        this.originalConsoleError.call(console, 'Global error:', error, errorInfo);
    };

    /**
     * Handle unhandled promise rejections
     */
    handleUnhandledRejection = (event) => {
        const error = event.reason instanceof Error
            ? event.reason
            : new Error(event.reason || 'Unhandled promise rejection');

        const errorInfo = {
            type: 'unhandled_promise_rejection',
            promise: event.promise
        };

        graylogService.logError(error, errorInfo);

        // Call original console.error for development
        this.originalConsoleError.call(console, 'Unhandled promise rejection:', error, errorInfo);
    };

    /**
     * Override console methods to capture errors only (no warnings)
     */
    overrideConsoleMethods() {
        const self = this;

        console.error = function (...args) {
            // Send to Graylog only if it looks like an error and NOT a warning
            const errorMessage = args.join(' ');
            const isWarning = errorMessage.toLowerCase().includes('warning') ||
                errorMessage.toLowerCase().includes('warn');
            const isGelf = errorMessage.toLowerCase().includes('gelf');
            const isGraylog = errorMessage.toLowerCase().includes('graylog');

            if ((errorMessage.includes('Error') || errorMessage.includes('error')) && !isWarning && !isGelf && !isGraylog) {
                graylogService.sendLog('error', `Console Error: ${errorMessage}`, {
                    type: 'console_error',
                    args: args
                });
            }

            // Call original method
            self.originalConsoleError.apply(console, args);
        };

        // Keep console.warn as original - no logging to Graylog
        // console.warn = self.originalConsoleWarn;
    }

    // Removed page visibility and unload handlers - keeping only essential error logging

    // Removed user action and API error logging methods - keeping only essential error handling

    /**
     * Clean up event listeners
     */
    cleanup() {
        if (!this.isInitialized) {
            return;
        }

        window.removeEventListener('error', this.handleGlobalError);
        window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);

        // Restore original console methods
        console.error = this.originalConsoleError;
        // console.warn was never overridden, so no need to restore

        this.isInitialized = false;
    }
}

// Create singleton instance
const globalErrorHandler = new GlobalErrorHandler();

export default globalErrorHandler;
